import { useCallback, useEffect, useState } from 'react';

const DISMISS_KEY = 'pejmanPwaInstallDismissed';
const INSTALLED_KEY = 'pejmanPwaInstalled';

function isIosDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const iPadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iOS || iPadOs;
}

function isStandaloneDisplay() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches ||
    Boolean(window.navigator.standalone)
  );
}

/**
 * PWA install state for Pejman — Android/Chrome prompt + iOS Add to Home Screen.
 */
export default function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(() => isStandaloneDisplay());
  const [isIos] = useState(() => isIosDevice());
  const [dismissed, setDismissed] = useState(
    () => typeof sessionStorage !== 'undefined' && sessionStorage.getItem(DISMISS_KEY) === '1'
  );
  const [justInstalled, setJustInstalled] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      window.__pejmanDeferredPrompt = e;
    };

    const onInstalled = () => {
      setDeferredPrompt(null);
      window.__pejmanDeferredPrompt = null;
      setJustInstalled(true);
      setIsStandalone(true);
      try {
        localStorage.setItem(INSTALLED_KEY, '1');
      } catch (_) {
        /* ignore */
      }
    };

    const onDisplayChange = (mq) => {
      if (mq.matches) setIsStandalone(true);
    };

    // Prompt may have fired before React mounted
    if (window.__pejmanDeferredPrompt) {
      setDeferredPrompt(window.__pejmanDeferredPrompt);
    }

    const onCustomPrompt = () => {
      if (window.__pejmanDeferredPrompt) {
        setDeferredPrompt(window.__pejmanDeferredPrompt);
      }
    };

    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('pejman-pwa-prompt', onCustomPrompt);
    window.addEventListener('appinstalled', onInstalled);

    const mq = window.matchMedia('(display-mode: standalone)');
    if (mq.addEventListener) mq.addEventListener('change', onDisplayChange);
    else if (mq.addListener) mq.addListener(onDisplayChange);

    // Sync theme-color for installed / in-browser chrome
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', '#012E53');

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('pejman-pwa-prompt', onCustomPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      if (mq.removeEventListener) mq.removeEventListener('change', onDisplayChange);
      else if (mq.removeListener) mq.removeListener(onDisplayChange);
    };
  }, []);

  const canNativeInstall = Boolean(deferredPrompt) && !isStandalone;
  const canIosInstall = isIos && !isStandalone;
  const showBanner = !dismissed && !isStandalone && (canNativeInstall || canIosInstall);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch (_) {
      /* ignore */
    }
  }, []);

  const promptInstall = useCallback(async () => {
    const promptEvent = deferredPrompt || window.__pejmanDeferredPrompt;
    if (!promptEvent) return { outcome: 'unavailable' };
    promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    setDeferredPrompt(null);
    window.__pejmanDeferredPrompt = null;
    if (choice?.outcome === 'accepted') {
      setJustInstalled(true);
    }
    return choice || { outcome: 'dismissed' };
  }, [deferredPrompt]);

  return {
    isStandalone,
    isIos,
    canNativeInstall,
    canIosInstall,
    showBanner,
    justInstalled,
    dismiss,
    promptInstall
  };
}
